import type { ImageStyle, TextStyle, ViewStyle } from 'react-native';
import { BOARD_TILE_LEDGE } from '../constants/warmup.constants';

/**
 * Dynamic (runtime-computed) style fragments for the Warmup games.
 *
 * These cannot live in `StyleSheet.create()` because they depend on values only known at runtime
 * (measured tile `size`, a per-name avatar `hue`, the loaded banner `aspectRatio`, a card `accent`
 * prop). The project's styling rule bans inline style-object literals in JSX except for Animated
 * values, so each truly-dynamic style is built here and applied via a style array — mirroring the
 * sanctioned `bracketGeometry.helper.ts` pattern. Finite/enumerable styling (per-state palettes,
 * action vs. letter sizing) stays as named styles in the co-located `.styles.ts` files.
 */

/** Deterministic avatar hue (0-359) from the player's name (mirrors the design prototype). */
export function hueFromName(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) % 360;
  return h;
}

/** Avatar circle background tinted by the deterministic name hue. */
export function avatarHueStyle(hue: number): ViewStyle {
  return { backgroundColor: `hsl(${hue}, 55%, 38%)` };
}

/** Outer chip wrapper sized to the measured tile (square + the raised ledge below it). */
export function tileOuterSizeStyle(size: number): ViewStyle {
  return { width: size, height: size + BOARD_TILE_LEDGE };
}

/** Square face area at the measured tile size. */
export function tileFaceSizeStyle(size: number): ViewStyle {
  return { width: size, height: size };
}

/** Tile letter sized relative to the measured tile. */
export function tileTextSizeStyle(size: number): TextStyle {
  return { fontSize: Math.round(size * 0.46) };
}

/** Card border + shadow tinted by the per-game accent color. */
export function cardAccentStyle(accent: string): ViewStyle {
  return { borderColor: accent, shadowColor: accent };
}

/** Banner image aspect ratio measured from the loaded artwork. */
export function bannerAspectStyle(ratio: number): ImageStyle {
  return { aspectRatio: ratio };
}

/** Top padding equal to the device safe-area inset. */
export function screenInsetStyle(insetTop: number): ViewStyle {
  return { paddingTop: insetTop };
}
